<script setup lang="ts">
/**
 * The sign-in pages' frame (2026-10-12): on a wide screen, a 3D scene with a few words beside the
 * form (the calls' globe, or floating shapes); on a phone, the form alone, nothing heavy to load.
 */
import { computed } from 'vue'
import ThreeCanvas from '@/ui/three/ThreeCanvas.vue'
import { scenes } from '@/ui/three/scenes'
import { useWide } from '@/lib/useWide'

const p = withDefaults(defineProps<{ scene?: 'globe' | 'shapes'; title: string; body: string }>(), { scene: 'globe' })
/* The scene is loaded only where it shows: never on a phone. */
const wide = useWide()
const options = computed(() => (p.scene === 'globe' ? { arcs: 16 } : {}))
</script>

<template>
  <div class="grid min-h-full bg-muted/40 lg:grid-cols-[minmax(0,1.15fr)_minmax(440px,1fr)]">
    <section v-if="wide" class="relative min-h-[560px] overflow-hidden border-e border-border bg-card">
      <div class="absolute inset-0 bg-[radial-gradient(90%_70%_at_35%_30%,hsl(var(--primary)/.16),transparent_65%)]" />
      <ThreeCanvas :scene="scene === 'globe' ? scenes.globe : scenes.shapes" :options="options" pointer
                   class="absolute inset-x-0 top-0 h-[80%]" />
      <div class="pointer-events-none relative z-[1] flex h-full flex-col justify-end gap-2 p-10 xl:p-14">
        <h2 class="m-0 max-w-[20ch] animate-rise-in text-[30px] font-semibold leading-[1.15] tracking-[-.02em] [text-wrap:balance]">{{ title }}</h2>
        <p class="m-0 max-w-[50ch] animate-rise-in text-[14px] leading-[1.55] text-muted-foreground [animation-delay:120ms]">{{ body }}</p>
      </div>
    </section>
    <div class="grid place-items-center px-4 py-10"><slot /></div>
  </div>
</template>
